import {Injectable, Injector} from '@angular/core';
import * as _ from 'lodash-es';
import {BehaviorSubject, EMPTY, fromEvent, merge, Observable, Subject} from 'rxjs';
import {distinctUntilChanged, map, pairwise, startWith, switchMap, takeUntil, tap} from 'rxjs/operators';
import {DynamicComponentRef} from '../../../components/dynamic-component';
import {FloatCell, FloatCellDefinition, FloatLayout, FloatLayoutFactory} from '../../../components/float-layout';
import {
    addPoints,
    BoxDimensions,
    Dimension,
    Point,
    subtractPoints,
    X,
    Y
} from '../../../components/layout/abstract-units';
import {Cell} from '../../../components/layout/cell';
import {CellActionType} from '../../../components/layout/layout-actions/cell-action-type';
import {LayoutChanges} from '../../../components/layout/layout-changes';
import {Widget} from '../../../components/layout/widget/widget';
import {WidgetDefinition} from '../../../components/layout/widget/widget-definition';
import {WidgetFactory} from '../../../components/layout/widget/widget.factory';
import {ModalService} from '../../../components/modal';
import {WindowRefService} from '../../../services/window-ref.service';
import {FloatCellComponent} from './float-cell/float-cell.component';
import {Bin} from './float-placement/bin';
import {BinPlacementState} from './float-placement/common/bin-placement-state';
import {PlacedBinDef} from './float-placement/common/placed-bin-def';
import {FloatPlacement} from './float-placement/float-placement';

interface CellComponentInfo {
    cell: FloatCell;
    cellIndex: number;
    componentIndex: number;
    componentRef: DynamicComponentRef<FloatCellComponent>;
}

@Injectable()
export class FloatLayoutService {

    public floatLayout: FloatLayout;

    public layoutResize$: Observable<any>;

    /**
     * Defines the area where new float cells should be placed.
     * Should be defined by parent.
     */
    public placementBoardDimensions: BoxDimensions;

    private floatLayoutFactory: FloatLayoutFactory = this.injector.get(FloatLayoutFactory);
    private widgetFactory: WidgetFactory = this.injector.get(WidgetFactory);

    private floatCellComponents: Array<DynamicComponentRef<FloatCellComponent>> = [];

    private layoutResizeSubject$ = new Subject<void>();

    private readonly floatLayout$ = new BehaviorSubject<FloatLayout>(null);

    constructor(
        private injector: Injector,
        private modalService: ModalService,
        private windowRefService: WindowRefService
    ) {
        this.layoutResize$ = this.layoutResizeSubject$.asObservable();

        this.listenToFloatLayoutChange();
    }

    public setFloatLayout(floatLayout: FloatLayout): void {
        floatLayout = floatLayout || null;

        this.floatLayout$.next(floatLayout);
    }

    public createFloatCellByDefinition(
        floatCellDef: FloatCellDefinition,
        widgetDef: WidgetDefinition,
        parentCell?: Cell
    ): void {
        const cell: FloatCell = this.floatLayoutFactory.createFloatCellByDefinition(
            floatCellDef,
            this.floatLayout.size
        );

        this.widgetFactory.createWidgetByDefinition(widgetDef, cell);

        const changes: LayoutChanges = new LayoutChanges()
            .add('New float cell', {cell}, {actionType: CellActionType.CELL_NEW})
            .append(this.applyPlacement(cell, parentCell));

        this.floatLayout.registerCell(cell);
        this.floatLayout.updateCellsDisplayInfo([cell]);
        this.floatLayout.commitChanges(changes);
    }

    public hideAllCellComponents(): void {
        this.floatCellComponents.forEach((dynamicComponentRef: DynamicComponentRef<FloatCellComponent>) => {
            dynamicComponentRef.instance.isHidden = true;
        });
    }

    public showAllCellComponents(): void {
        this.floatCellComponents.forEach((dynamicComponentRef: DynamicComponentRef<FloatCellComponent>) => {
            dynamicComponentRef.instance.isHidden = false;
        });
    }

    public applyPlacement(cell: FloatCell, parentCell?: Cell): LayoutChanges {
        const changes = new LayoutChanges();
        const BINS_GAP = 4;
        const boardDim = this.getPlacementBoardDimensions(BINS_GAP);
        const floatPlacement = new FloatPlacement();

        const cellDim: BoxDimensions = this.getCellDisplayDimensions(cell);
        cellDim.pos = subtractPoints(cellDim.pos, boardDim.pos);

        const cellBin = new Bin(cellDim.size[X] + BINS_GAP * 2, cellDim.size[Y] + BINS_GAP * 2);

        const getFloatCellBinDef = (floatCell: FloatCell): PlacedBinDef => {
            const currentCellDim: BoxDimensions = this.getCellDisplayDimensions(floatCell);
            const [x, y] = subtractPoints(currentCellDim.pos, boardDim.pos);
            const [w, h] = currentCellDim.size;

            return {x, y, h, w};
        };
        const placedBinDefs: PlacedBinDef[] = this.floatLayout.cells.map(getFloatCellBinDef);

        if (parentCell) {
            const isParentCellFloat: boolean = !parentCell.edges;
            let parentCellBinDef: PlacedBinDef;

            if (isParentCellFloat) {
                parentCellBinDef = getFloatCellBinDef(parentCell as FloatCell);
            } else if (parentCell.display.cellComponent) {
                const {left: layoutCellLeft, top: layoutCellTop, width: layoutCellWidth, height: layoutCellHeight} = (
                    (parentCell.display.cellComponent as any /* CellComponent */) // TODO
                        .cellElementRef.nativeElement as HTMLElement
                ).getBoundingClientRect();

                parentCellBinDef = {
                    x: BINS_GAP + layoutCellLeft - boardDim.pos[X],
                    y: BINS_GAP + layoutCellTop - boardDim.pos[Y],
                    w: layoutCellWidth - BINS_GAP * 2,
                    h: layoutCellHeight - BINS_GAP * 2
                };
            }

            if (placedBinDefs && parentCellBinDef) {
                parentCellBinDef.isParent = true;
                placedBinDefs.unshift(parentCellBinDef);
            }
        }

        const variants: BinPlacementState[] = floatPlacement
            .initBoard(boardDim.size[X], boardDim.size[Y], BINS_GAP)
            .initBins(placedBinDefs, def => def)
            .produceVariants(cellBin);
        const bestVariant = variants[0];

        if (bestVariant) {
            this.floatLayout.setCellDisplayPos(cell, [
                bestVariant.placedBin.x + boardDim.pos[X] + BINS_GAP,
                bestVariant.placedBin.y + boardDim.pos[Y] + BINS_GAP
            ]).appendTo(changes);
        }

        return changes;
    }

    private getPlacementBoardDimensions(binsGap: number): BoxDimensions {
        const layoutDimensions: BoxDimensions = this.placementBoardDimensions || {
            pos:  [binsGap, binsGap],
            size: this.floatLayout.displaySize
        };
        const boardSidePadding: Point = [binsGap, binsGap];

        return {
            pos: addPoints(layoutDimensions.pos, boardSidePadding),
            size: subtractPoints(layoutDimensions.size, [boardSidePadding[X] * 2, boardSidePadding[Y] * 2])
        };
    }

    private getCellDisplayDimensions(cell: FloatCell): BoxDimensions {
        const getCellComponentDimensions = this.getCellComponentDimensions.bind(this);
        const cellComponentMeta = {
            get dimensions(): BoxDimensions {
                const dimensions = getCellComponentDimensions(cell);
                this.dimensions = dimensions;

                return dimensions;
            },
            set dimensions(dimensions: BoxDimensions) {
                delete this.dimensions;
                this.dimensions = dimensions;
            }
        };
        const widget: Widget = cell.activeWidget;
        const widgetDefaultWidgetSize: Point = widget.widgetDescription.defaultWidgetSize;

        const fallbackDimension = (...valueGetters: Array<() => number>) => {
            let result: number = null;
            valueGetters.some(valueGetter => {
                result = valueGetter();

                return result === 0 || result > 0; // Eliminate null, undefined, NaN and so on (null >= 0 - it's true).
            });

            return result;
        };

        const size: Point = [Dimension.X, Dimension.Y]
            .map(dimension => fallbackDimension(
                () => cell.display && cell.display.size[dimension],
                () => cellComponentMeta.dimensions.size[dimension],
                () => widgetDefaultWidgetSize && widgetDefaultWidgetSize[dimension]
            )) as Point;
        const pos: Point = [Dimension.X, Dimension.Y]
            .map(dimension => fallbackDimension(
                () => cell.display && cell.display.pos[dimension],
                () => cellComponentMeta.dimensions.pos[dimension],
                () => Math.ceil((this.floatLayout.displaySize[dimension] - size[dimension]) / 2)
            )) as Point;

        return {pos, size};
    }

    private renderAllCells(): void {
        this.floatLayout.cells.forEach(cell => this.renderCell(cell));
    }

    private destroyAllCellComponents(): void {
        this.floatLayout.cells.forEach(cell => this.destroyCellComponent(cell));
    }

    private renderCell(cell: FloatCell): void {
        const cellComponentRef: DynamicComponentRef<FloatCellComponent> = this.modalService
            .open(FloatCellComponent, {
                injector:    this.injector,
                destroyWhen: this.floatLayout.destroy$,
                configure:   {
                    cell,
                    layoutResize$: this.layoutResize$,
                    floatLayout:   this.floatLayout
                }
            });

        this.floatCellComponents.push(cellComponentRef);
    }

    private destroyCellComponent(cell: FloatCell): void {
        const cellComponentRef: DynamicComponentRef<FloatCellComponent> = this.floatCellComponents
            .find(componentRef => componentRef.instance.cell === cell);

        if (cellComponentRef) {
            cellComponentRef.destroy();

            _.pull(this.floatCellComponents, cellComponentRef);
        }
    }

    private updateCellComponentIndex(cellInfo: CellComponentInfo): void {
        const componentIndex: number = cellInfo.componentIndex;
        const lastComponentIndex: number = this.floatCellComponents.length - 1;
        const newComponentIndex: number = Math.min(lastComponentIndex, cellInfo.cellIndex);

        if (componentIndex !== newComponentIndex) {
            const ref: DynamicComponentRef<FloatCellComponent> = cellInfo.componentRef;

            this.floatCellComponents.splice(componentIndex, 1);

            if (newComponentIndex < lastComponentIndex) {
                ref.insertBefore(this.floatCellComponents[newComponentIndex]);
                this.floatCellComponents.splice(newComponentIndex, 0, ref);
            } else {
                ref.insertAfter(this.floatCellComponents[lastComponentIndex]);
                this.floatCellComponents.push(ref);
            }
        }
    }

    private updateCellComponentIndexes(cells: FloatCell[]): void {
        const layoutCells: FloatCell[] = this.floatLayout.cells;
        const hasOrderChanges: boolean = cells.some(cell => {
            const cellIndex: number = layoutCells.indexOf(cell);
            const componentIndex: number = this.getCellComponentIndex(cell);

            return cellIndex !== componentIndex
                && cellIndex >= 0 && componentIndex >= 0;
        });

        if (hasOrderChanges) {
            layoutCells
                .map<CellComponentInfo>(cell => {
                    const componentIndex: number = this.getCellComponentIndex(cell);

                    return {
                        cell,
                        cellIndex: layoutCells.indexOf(cell),
                        componentIndex,
                        componentRef: this.floatCellComponents[componentIndex]
                    };
                })
                .filter(cellInfo =>
                    cellInfo.cellIndex !== cellInfo.componentIndex
                    && cellInfo.cellIndex >= 0 && cellInfo.componentIndex >= 0
                )
                .sort((cellInfo1: CellComponentInfo, cellInfo2: CellComponentInfo) =>
                    cellInfo1.cellIndex - cellInfo2.cellIndex // ASC
                )

                // Don't touch the top element in order to not break the current browser events chain.
                // Move elements around it instead.
                .slice(0, -1)
                .forEach(cellInfo => this.updateCellComponentIndex(cellInfo));
        }
    }

    private applyLayoutChanges(changes: LayoutChanges): void {
        (changes.getChangesByActionType(CellActionType.CELL_DELETE).cells as FloatCell[])
            .forEach(cell => this.destroyCellComponent(cell));

        (changes.getChangesByActionType(CellActionType.CELL_NEW).cells as FloatCell[])
            .forEach(cell => this.renderCell(cell));

        this.updateCellComponentIndexes(
            changes.getChangesByActionType(CellActionType.CELL_CHANGE).cells as FloatCell[]
        );
    }

    private listenToLayoutResize(): void {
        fromEvent(this.windowRefService.nativeWindow, 'resize').pipe(
            takeUntil(this.floatLayout.destroy$),
            startWith(null),
            map<any, Point>(() => [
                this.windowRefService.nativeWindow.innerWidth,
                this.windowRefService.nativeWindow.innerHeight
            ]),
            tap(size => this.floatLayout.produceDisplayInfo(size)),
            tap(() => this.layoutResizeSubject$.next())
        ).subscribe();
    }

    private getCellComponentIndex(cell: FloatCell): number {
        return this.floatCellComponents
            .findIndex(componentRef => componentRef.instance.cell === cell);
    }

    private getCellComponentDimensions(cell: FloatCell): BoxDimensions {
        let dimensions: BoxDimensions;
        const ref = this.floatCellComponents
            .find(componentRef => componentRef.instance.cell === cell);

        if (ref) {
            const {left, top, width, height} = (ref.instance.elementRef.nativeElement as HTMLElement)
                .getBoundingClientRect();

            dimensions = {
                pos: [left, top],
                size: [width, height]
            };
        } else {
            dimensions = {
                pos: [null, null],
                size: [null, null]
            };
        }

        return dimensions;
    }

    private listenToFloatLayoutChange(): void {
        this.floatLayout$.pipe(
            distinctUntilChanged(),
            pairwise(),
            map(([prevFloatLayout, floatLayout]: FloatLayout[]) => {
                if (prevFloatLayout) {
                    this.destroyAllCellComponents();
                }

                return floatLayout;
            }),
            tap(floatLayout => {
                this.floatLayout = floatLayout;

                if (floatLayout) {
                    this.renderAllCells();
                    this.listenToLayoutResize();
                }

                return floatLayout;
            }),
            switchMap(floatLayout => floatLayout
                ? merge(
                    floatLayout.update$.pipe(
                        tap(changes => this.applyLayoutChanges(changes))
                    ),
                    floatLayout.destroy$.pipe(
                        tap(() => this.destroyAllCellComponents())
                    )
                )
                : EMPTY
            )
        ).subscribe();
    }

}
